import { ChevronDownIcon } from "@heroicons/react/outline";
import React, { useMemo } from "react";
import classNames from "../utils/classNames";
import CenteredLoading from "./CenteredLoading";

interface Props {
  className?: string;
  title?: string | React.ReactNode;
  opened: boolean;
  setOpened?: (collapsed: boolean) => void;
  icon: JSX.Element | string;
  saveButtonName?: string;
  cancelButtonName?: string;
  onClickButton?: (type: "save" | "cancel") => void;
  loadingSave?: boolean;
  disabledSave?: boolean;
  withCollapse?: boolean;
}

const CollapsedBox: React.FC<Props> = ({
  className = "",
  title,
  opened,
  children,
  icon,
  saveButtonName,
  cancelButtonName,
  disabledSave,
  loadingSave = false,
  onClickButton,
  setOpened,
  withCollapse = true
}) => {
  const renderCollapseButton = useMemo(
    () => (
      <button
        type="button"
        onClick={() => {
          setOpened && setOpened(true);
        }}
        className={classNames(
          "inline-flex rounded-md p-1.5 focus:outline-none hover:text-white dark:text-white dark:hover:bg-gray-900 hover:bg-hotspotty-400"
        )}
      >
        <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    ),
    [setOpened]
  );

  return (
    <div
      className={classNames(
        "rounded-lg border bg-white shadow-md dark:bg-gray-800 dark:border-gray-500",
        className
      )}
    >
      <div className="flex w-full items-center justify-between py-4 px-3 sm:px-4">
        <h3 className="flex items-center text-md sm:text-lg font-bold dark:text-white w-full">
          <span className="mr-1">{icon}</span>
          {title}
        </h3>

        {withCollapse && !opened && renderCollapseButton}
      </div>

      {opened && (
        <>
          <div className="border-t dark:border-gray-500">{children}</div>
          <div
            className={classNames(
              (cancelButtonName || saveButtonName) &&
                "flex items-center justify-center sm:justify-end py-4 px-3 sm:px-4 border-t dark:border-gray-500"
            )}
          >
            {cancelButtonName && (
              <button
                className="w-full sm:w-auto text-xs py-1.5 px-6 mr-4 sm:mr-0 rounded dark:text-white border dark:border-transparent dark:bg-gray-700 dark:hover:opacity-75 outline-none focus:outline-none"
                onClick={() => onClickButton && onClickButton("cancel")}
              >
                {cancelButtonName}
              </button>
            )}
            {saveButtonName && (
              <button
                disabled={loadingSave || disabledSave}
                className="w-full sm:w-auto text-xs sm:ml-3 py-1.5 px-8 rounded border text-white bg-hotspotty-400 hover:bg-hotspotty-400 border-hotspotty-400 outline-none focus:outline-none disabled:opacity-75 disabled:hover:cursor-default disabled:hover:bg-hotspotty-400"
                onClick={() =>
                  onClickButton &&
                  !loadingSave &&
                  !disabledSave &&
                  onClickButton("save")
                }
              >
                {loadingSave ? (
                  <span className="flex items-center">
                    <CenteredLoading className="w-5 h-4 mr-2" /> Processing...
                  </span>
                ) : (
                  saveButtonName
                )}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CollapsedBox;
